import { useRouter } from "next/router";
import {
  useEffect,
  // useRef
} from "react";
import { useQuery } from "react-query";
import { Box } from "@chakra-ui/react";
import styled from "@emotion/styled";
import {
  ColorIcon,
  colors,
  GoiLogo,
  HLine,
  HStack,
  MQ,
  NextLink,
  setVisitedFeature,
  Spacing,
  useResponsive,
  VLine,
  VStack,
  customEvent,
  EventTriggerAndTargetType,
} from "goi_common";

import { Tip } from "@/constants/tip";

import GuidebookTipContent from "@/components/Guidebook/GuidebookTipContent";
import MaxWidth1200Layout from "@/components/Layout/MaxWidth1200Layout";

import { ArticleMeta, getArticleMeta } from "containers/GuidebookContainer/remotes/getArticleMeta";
import { TipIdProps } from "pages/guidebook/tip/[category]/[index]";

import GuideBookBackCTASection from "@/components/Guidebook/GuideBookBackCTASection";
import { GuidebookDetailBannerSection } from "./components/GuidebookTipDetailBannerSection";
import PrepaidBanners from "@/components/common/PrepaidBanners";

const useSetVisitedFeature = ({ meta }: { meta?: ArticleMeta }) => {
  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) return;

    const isConversionContent = meta?.flags.includes("conversion");

    if (isConversionContent) {
      setVisitedFeature("guidebook-conversion");
    }

    if (router.query.category === "before") {
      setVisitedFeature("guidebook-before");
    }
  }, [router, meta]);
};

export default function GuidebookTipContainer({ category, index, content }: TipIdProps) {
  const { data: meta } = useQuery(
    ["article_meta", category, index],
    () => getArticleMeta({ type: "tip", category, index }),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const { isBiggerThanLg } = useResponsive();

  useSetVisitedFeature({ meta });

  return (
    <>
      <MaxWidth1200Layout maxWidth={700}>
        <Box
          css={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            margin: "0 auto",
          }}
        >
          <GuidebookBreadCrumb
            item={{
              text: content.title,
              href: content.index,
            }}
          />

          <GuidebookDetailTitleSection views={meta?.views || 0} content={content} />

          <GuidebookDetailBannerSection />

          <ContentContainer>
            <Content>
              <GuidebookTipContent
                contents={content.contents}
                category={category}
                index={index}
                html={content.html}
              />
            </Content>
          </ContentContainer>
        </Box>
        <PrepaidBanners eventPath="guidebook" isMobile title="장례 준비, 미리 알아보기" />
        <Box margin="0px 20px 40px 20px">
          <HLine color={colors.gray300} />
          <HStack padding="13px 0" alignItems="center" gap="12px">
            <Box
              padding="11px 12px 11px 10px"
              borderRadius="50%"
              border={`1px solid ${colors.gray100}`}
            >
              <GoiLogo color={"black"} size={isBiggerThanLg ? 30 : 24} />
            </Box>
            <VStack>
              <div className="body_14_m gray700">작성자</div>
              <div className="subtitle_14_sb">고이장례연구소 콘텐츠팀</div>
            </VStack>
          </HStack>
          <HLine color={colors.gray300} />
        </Box>

        <Box padding="0px">
          <HLine color={colors.gray50} height={10} />
        </Box>

        <Box padding="47px 0">
          <GuideBookBackCTASection />
        </Box>
        <Spacing gap={80} />
      </MaxWidth1200Layout>

      {/* {isFuneralExpense && isBiggerThanLg && <FuneralExpenseFooter ref={funeralExpenseRef} />} */}
    </>
  );
}

const ContentContainer = styled.div`
  padding: 18px 20px;

  > .subtitle2_b {
    padding-bottom: 4px;
  }
`;

const Content = styled.div`
  padding: 20px 0 80px;
  color: var(--gray800);

  ${MQ.lg} {
    h1 {
      font-size: 24px;
      font-weight: 700;
      line-height: 32px;
    }
    h2 {
      font-size: 21px;
      font-weight: 700;
      line-height: 30px;
    }
    h3 {
      font-size: 18px;
      font-weight: 700;
      line-height: 26px;
    }
    p {
      font-size: 18px;
      font-weight: 400;
      line-height: 28px;
    }
  }

  > section + section {
    padding-top: 48px;
  }

  > section > section {
    > strong {
      display: block;

      + div {
        padding-top: 8px;
      }
    }

    + section {
      padding-top: 24px;
    }
  }

  .subtitle2_b {
    + section {
      padding-top: 8px;
    }
  }

  a {
    text-decoration: underline;
  }

  section {
    overflow: scroll;

    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

interface GuidebookBreadCrumbProps {
  item: {
    text: string;
    href: string;
  };
}

const GuidebookBreadCrumb = ({ item }: GuidebookBreadCrumbProps) => {
  const onClickSubmitCustomEvent = (trigger_and_target: EventTriggerAndTargetType["gdb_tip"]) => {
    customEvent({
      newGtm: {
        path: "gdb_tip",
        trigger_and_target,
      },
    });
  };

  return (
    <nav>
      <HStack as="ul" padding="16px 20px" gap="4px" alignItems="center">
        <li onClick={() => onClickSubmitCustomEvent("breadcrumb-uv_home")}>
          <NextLink href="/">
            <ColorIcon name="icon-home-mono" color={colors.gray700} size={16} />
          </NextLink>
        </li>
        <li>
          <ColorIcon name="icon-arrow-right-small-mono" color={colors.gray500} size={14} />
        </li>
        <li
          onClick={() => onClickSubmitCustomEvent("breadcrumb-uv_guidebook")}
          css={{
            whiteSpace: "nowrap",
          }}
        >
          <NextLink href="/guidebook/">가이드북</NextLink>
        </li>
        <li>
          <ColorIcon name="icon-arrow-right-small-mono" color={colors.gray500} size={14} />
        </li>
        <li
          onClick={() => onClickSubmitCustomEvent("breadcrumb-uv_self")}
          css={{
            display: "-webkit-box",
            textOverflow: "ellipsis",
            overflow: "hidden",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
          }}
        >
          <NextLink href={item.href}>{item.text}</NextLink>
        </li>
      </HStack>
    </nav>
  );
};

interface GuidebookDetailTitleSectionProps {
  views: number;
  content: Tip;
}

const GuidebookDetailTitleSection = ({ views, content }: GuidebookDetailTitleSectionProps) => {
  const { tag, title, date } = content;

  return (
    <section
      className="gray700"
      css={{
        padding: "60px 20px 20px 20px",
        textAlign: "center",
        position: "relative",
      }}
    >
      <Box className="subtitle_14_sb lg_subtitle_21_m">{tag}</Box>
      <Box as="h1" className="head_24_sb lg_head_42_b gray900" margin="10px 12px">
        {title}
      </Box>
      <HStack
        className="body_14_m lg_subtitle_21_m"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box flex="1" textAlign="right">
          {date}
        </Box>
        <VLine height="14px" margin="4px 15px" color={colors.gray500} />
        <Box flex="1" textAlign="left">
          조회수 {views}
        </Box>
      </HStack>
    </section>
  );
};
